/* eslint-disable react/prop-types */
import {
  Card,
  Box,
  TextField,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Add,
  Delete,
  DeleteForeverOutlined as DeleteForeverOutlinedIcon,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getSingleSelectStyles } from "@/lib/styles";

const animatedComponents = makeAnimated();

const StepInputCard = ({
  index,
  step_input,
  showOptionsField,
  handleInputTypeChange,
  moveStepInputUp,
  moveStepInputDown,
  handleDeleteStepInput,
  optionInputs,
  handleOptionInputChange,
  handleAddOption,
  removeOption,
  inputTypeList,
  totalInputs,
  translationPrefix = "newWorkFlow",
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const singleSelectStyles = getSingleSelectStyles(theme);

  return (
    <Card
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 1,
        boxShadow: 4,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
        <IconButton
          size="small"
          onClick={() => moveStepInputUp(index)}
          disabled={index === 0}
          aria-label={t(`${translationPrefix}.moveStepInputUpAria`, {
            index: index + 1,
          })}
        >
          <ArrowUpward />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => moveStepInputDown(index)}
          disabled={index === totalInputs - 1}
          aria-label={t(`${translationPrefix}.moveStepInputDownAria`, {
            index: index + 1,
          })}
        >
          <ArrowDownward />
        </IconButton>
        <IconButton
          onClick={() => handleDeleteStepInput(index)}
          color="error"
          aria-label={t(`${translationPrefix}.removeStepInputAria`, {
            index: index + 1,
          })}
        >
          <DeleteForeverOutlinedIcon />
        </IconButton>
      </Box>

      <Grid container spacing={1}>
        <Grid size={12}>
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, lg: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                required
                options={inputTypeList}
                components={animatedComponents}
                closeMenuOnSelect={true}
                value={
                  inputTypeList.find(
                    (option) => option.value === step_input.input_type_id
                  ) || null
                }
                onChange={(selectedOption) =>
                  handleInputTypeChange(index, "input_type_id", selectedOption)
                }
                styles={{
                  ...singleSelectStyles,
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 2000,
                  }),
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder={t(`${translationPrefix}.inputTypePlaceholder`)}
                aria-label={t(`${translationPrefix}.inputTypeAria`, {
                  index: index + 1,
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t(`${translationPrefix}.nameLabel`)}
                required
                fullWidth
                variant="outlined"
                size="normal"
                value={step_input.name || ""}
                onChange={(e) =>
                  handleInputTypeChange(index, "name", e.target.value)
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    background: theme.palette.background.default,
                  },
                }}
                aria-label={t(`${translationPrefix}.nameAria`, {
                  index: index + 1,
                })}
              />
            </Grid>
            {showOptionsField && (
              <Grid size={{ xs: 12, md: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <TextField
                    label={t(`${translationPrefix}.addOptionLabel`)}
                    value={optionInputs[index] || ""}
                    onChange={(e) =>
                      handleOptionInputChange(index, e.target.value)
                    }
                    fullWidth
                    variant="outlined"
                    size="normal"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        background: theme.palette.background.default,
                      },
                    }}
                    aria-label={t(`${translationPrefix}.addOptionAria`, {
                      index: index + 1,
                    })}
                  />
                  <IconButton
                    onClick={() => handleAddOption(index)}
                    disabled={!optionInputs[index]?.trim()}
                    sx={{
                      ml: 1,
                      color: theme.palette.success.main,
                      "&:hover": {
                        bgcolor: theme.palette.success.light,
                      },
                    }}
                    aria-label={t(`${translationPrefix}.addOptionButtonAria`, {
                      index: index + 1,
                    })}
                  >
                    <Add />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {step_input.options?.map((option, optIndex) => (
                    <Chip
                      key={optIndex}
                      label={option}
                      onDelete={() => removeOption(index, optIndex)}
                      deleteIcon={<Delete />}
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                      }}
                      aria-label={t(`${translationPrefix}.optionChipAria`, {
                        option,
                        index: index + 1,
                      })}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t(`${translationPrefix}.defaultValueLabel`)}
                fullWidth
                variant="outlined"
                size="normal"
                value={step_input.default_value || ""}
                onChange={(e) =>
                  handleInputTypeChange(index, "default_value", e.target.value)
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    background: theme.palette.background.default,
                  },
                }}
                aria-label={t(`${translationPrefix}.defaultValueAria`, {
                  index: index + 1,
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t(`${translationPrefix}.descriptionLabel`)}
                fullWidth
                variant="outlined"
                size="normal"
                value={step_input.description || ""}
                onChange={(e) =>
                  handleInputTypeChange(index, "description", e.target.value)
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    background: theme.palette.background.default,
                  },
                }}
                aria-label={t(`${translationPrefix}.descriptionAria`, {
                  index: index + 1,
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    background: theme.palette.background.default,
                  },
                }}
                disabled
                label={t(`${translationPrefix}.positionLabel`)}
                fullWidth
                variant="outlined"
                size="normal"
                type="number"
                value={step_input.position ?? ""}
                onChange={(e) =>
                  handleInputTypeChange(index, "position", e.target.value)
                }
                slotProps={{
                  inputLabel: {
                    min: 0,
                    step: 1,
                    pattern: "[0-9]*",
                  },
                }}
                error={isNaN(step_input.position) || step_input.position < 0}
                helperText={
                  isNaN(step_input.position) || step_input.position < 0
                    ? t(`${translationPrefix}.errorInvalidPosition`)
                    : ""
                }
                aria-label={t(`${translationPrefix}.positionAria`, {
                  index: index + 1,
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={step_input.required || false}
                    onChange={(e) =>
                      handleInputTypeChange(index, "required", e.target.checked)
                    }
                    aria-label={t(`${translationPrefix}.requiredCheckboxAria`, {
                      index: index + 1,
                    })}
                  />
                }
                label={t(`${translationPrefix}.requiredLabel`)}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

export default StepInputCard;
